import { useForm } from '@tanstack/react-form';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { z, ZodError } from 'zod';

import styles from '../styles.module.css';
import Button from '@/components/LoginComponents/Button';
import Input from '@/components/LoginComponents/Input';
import { POOL_NAME } from '@/constants';
import { useModal } from '@/context/ModalContext';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { clearStatus, fetchPasswordReset } from '@/slices/authSlice';
import { RootState } from '@/store';

const changePasswordSchema = z
  .object({
    newPassword: z.string().min(1, 'New password should not be empty'),
    reNewPassword: z.string().min(1, 'Confirm new password should not be empty')
  })
  .refine(data => data.newPassword === data.reNewPassword, {
    message: 'Passwords do not match',
    path: ['reNewPassword']
  });

function ChangePassword() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const errorResponse = auth?.error?.description;

  const { updateModalContent, closeModal } = useModal();

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    defaultValues: {
      newPassword: '',
      reNewPassword: ''
    },
    validators: {
      onSubmit: changePasswordSchema
    },
    onSubmit: async ({ value }) => {
      try {
        setIsLoading(true);
        await dispatch(
          fetchPasswordReset({
            username: auth?.data?.username || '',
            password: auth?.data?.password || '',
            newPassword: value.newPassword,
            reNewPassword: value.reNewPassword,
            poolName: POOL_NAME
          })
        );
      } finally {
        setIsLoading(false);
      }
    }
  });

  useEffect(() => {
    if (auth?.error?.code === 0 && auth?.status?.code === 200 && !isLoading) {
      updateModalContent(null, {
        modalTitle: 'Change Password',
        modalButtons: [
          {
            label: 'OK',
            onClick: () => {
              dispatch(clearStatus());
              navigate('/login');
              closeModal();
            },
            variant: 'okBlue'
          }
        ],
        alignButtons: 'center',
        contentTitle: auth?.status?.message
      });
    }
  }, [auth?.error?.code, auth?.status?.message, isLoading]);

  useEffect(() => {
    dispatch(clearStatus());
  }, [dispatch]);

  return (
    <div className={styles.form}>
      {errorResponse && (
        <div className={styles.formErrors}>{errorResponse}</div>
      )}

      <h3>Change Password</h3>
      <p className={styles.formDescription}>
        For security purposes please change your password
      </p>
      <form.Field name="newPassword">
        {field => (
          <Input
            type="text"
            placeholder="Enter new password"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>
      <form.Field name="reNewPassword">
        {field => (
          <Input
            type="text"
            placeholder="Confirm Password"
            value={field.state.value}
            onChange={value => field.handleChange(value || '')}
            errors={field?.state?.meta?.errors as ZodError[]}
          />
        )}
      </form.Field>

      <Button
        label="SUBMIT"
        isLoading={isLoading}
        onClick={() => form.handleSubmit()}
      />
    </div>
  );
}

export default ChangePassword;
